import React, { useState } from 'react';
import './FAQPage.css';

const FAQS = [
  {
    category: 'Service',
    items: [
      {
        q: 'Is Irish Visa Tracker an official Irish government service?',
        a: 'No. Irish Visa Tracker is an independent third-party service. We use publicly available data published by Irish Immigration (NDVO) at ireland.ie/en/india/newdelhi and are not affiliated with the Government of Ireland. We provide monitoring and notification services only — we do not process, decide, or expedite visa applications.'
      },
      {
        q: 'How does the service work?',
        a: 'We check official NDVO decision publications for your registered reference. When a new publication contains a matching decision, we prepare an email alert. Browser alerts are optional on supported devices.'
      },
      {
        q: 'Where does your data come from?',
        a: 'Directly from the official NDVO (New Delhi Visa Office) decisions file published on ireland.ie. We never estimate or guess decisions — every alert corresponds to a row in an official public file.'
      }
    ]
  },
  {
    category: 'Alerts',
    items: [
      {
        q: 'What kinds of alerts will I receive?',
        a: 'We alert you when an approval or refusal for your registered reference appears in an official NDVO decision publication. We cannot see unpublished case updates.'
      },
      {
        q: 'How fast are the alerts compared to checking manually?',
        a: 'We check for new publications automatically during the monitoring window, so you do not need to refresh the page yourself. Delivery time depends on publication, processing and the email or browser provider.'
      },
      {
        q: 'Can I receive alerts for more than one application?',
        a: 'Checkout registers one eight-digit visa reference. If you need alerts for another application, contact support from your purchase email so we can confirm its monitoring setup.'
      },
      {
        q: 'How do web push notifications work?',
        a: 'After upgrading, you can enable browser notifications on a supported device. Alerts require browser permission and an active subscription on that device. Email remains the primary channel.'
      }
    ]
  },
  {
    category: 'Pricing',
    items: [
      {
        q: 'How much does the premium upgrade cost?',
        a: '₹349 (one-time, in Indian Rupees). There is no subscription, no recurring charge, and no auto-renewal. Pay once and use the alerts and all 13 premium tools for the lifetime of your application.'
      },
      {
        q: 'What does the ₹349 upgrade include?',
        a: 'Decision email alerts for your registered references, optional browser notifications on supported devices, unlimited searches for your registered reference, and access to all 13 premium planning tools.'
      },
      {
        q: 'Is the price in Indian Rupees?',
        a: 'Yes. The price is ₹349 INR. If you are paying from outside India, your card issuer will convert the amount at the prevailing exchange rate.'
      }
    ]
  },
  {
    category: 'Refunds & cancellation',
    items: [
      {
        q: 'What is your refund policy?',
        a: 'Premium is a one-time, non-refundable purchase. Monitoring setup and tool access begin after payment verification, so we do not offer change-of-mind or unused-access refunds. For billing errors or service problems, email visahelp@irelandvisatracker.in with your transaction ID. See our refund policy for details; rights required by law remain unaffected.'
      },
      {
        q: 'Can I cancel the alerts later?',
        a: 'There is nothing to cancel — the upgrade is one-time, not a subscription. You can stop receiving alerts at any time by emailing us to remove your reference numbers from monitoring.'
      }
    ]
  },
  {
    category: 'Privacy & data',
    items: [
      {
        q: 'What personal data do you store?',
        a: 'We store the email address you provide at checkout and the visa reference numbers you ask us to monitor. We do not store passport details, financial information, or biometric data. Payments are processed by PayU; we never see or store your card details.'
      },
      {
        q: 'Do you sell or share my data?',
        a: 'No. We do not sell, rent, or share your data with third parties for marketing. We use Resend for email delivery and Supabase for storage, both of which are bound by strict data-processing agreements.'
      },
      {
        q: 'How do I request deletion of my data?',
        a: 'Email visahelp@irelandvisatracker.in from the address you used at checkout. We will delete your email, your reference numbers, and any alert history within 7 business days and confirm in writing.'
      }
    ]
  },
  {
    category: 'Accuracy & limitations',
    items: [
      {
        q: 'How accurate is your processing time data?',
        a: 'Our processing time tool provides indicative planning ranges from historical category averages. It is not an official deadline or a guarantee from Irish Immigration, so check current official updates before making plans.'
      },
      {
        q: 'What if my reference number does not appear in the daily file?',
        a: 'Some applications are decided outside the normal batch and published separately. If your reference has not appeared in any file after your estimated decision window, email us and we will investigate manually.'
      }
    ]
  }
];

const FaqPage = () => {
  const [openKey, setOpenKey] = useState(null);
  let counter = 0;
  const schemaItems = [];

  return (
    <div className="faq-page">
      <div className="faq-hero">
        <h1>Frequently Asked Questions</h1>
        <p className="faq-subtitle">
          Everything you need to know about tracking your Ireland visa application
          and getting decision alerts.
        </p>
      </div>

      <div className="faq-sections">
        {FAQS.map((section) => (
          <section key={section.category} className="faq-section">
            <h2>{section.category}</h2>
            {section.items.map((item) => {
              counter += 1;
              const key = `${section.category}-${counter}`;
              const open = openKey === key;
              schemaItems.push({ q: item.q, a: item.a });
              return (
                <div
                  key={key}
                  className={`faq-page-item${open ? ' open' : ''}`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <button
                    type="button"
                    className="faq-page-question"
                    aria-expanded={open}
                    onClick={() => setOpenKey(open ? null : key)}
                  >
                    <span itemProp="name">{item.q}</span>
                    <span className="faq-page-toggle" aria-hidden="true">
                      {open ? '−' : '+'}
                    </span>
                  </button>
                  {open && (
                    <div
                      className="faq-page-answer"
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                    >
                      <p itemProp="text">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        ))}
      </div>

      <div className="faq-cta">
        <h2>Still have a question?</h2>
        <p>
          Email <a href="mailto:visahelp@irelandvisatracker.in">visahelp@irelandvisatracker.in</a>
          {' '}if you need help.
        </p>
      </div>

      {/* JSON-LD is generated here so each route can own its schema. The
          actual injection is done by the parent route's <MetaTags schema=…> */}
      <script
        type="application/ld+json"
        data-ivt-static
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: schemaItems.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a }
            }))
          })
        }}
      />
    </div>
  );
};

export default FaqPage;
